import { useState } from "react";
import { motion } from "framer-motion";
import { Send, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { z } from "zod";

const inquirySchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(200),
  email: z.string().trim().email("Please enter a valid email").max(320),
  phone: z.string().trim().max(30).optional().or(z.literal("")),
  message: z.string().trim().min(1, "Message is required").max(2000),
});

const InquirySection = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = inquirySchema.safeParse(form);
    if (!parsed.success) {
      toast.error(parsed.error.issues[0].message);
      return;
    }

    setLoading(true);
    const payload = {
      name: parsed.data.name,
      email: parsed.data.email,
      phone: parsed.data.phone || null,
      message: parsed.data.message,
    };

    const { error } = await supabase.from("inquiries").insert(payload);
    setLoading(false);

    if (error) {
      toast.error("Something went wrong. Please try again.");
      return;
    }

    toast.success("Inquiry sent!", {
      description: "Thank you for reaching out. Coach Angela will get back to you soon.",
      duration: 5000,
    });
    setForm({ name: "", email: "", phone: "", message: "" });
  };

  const inputClasses =
    "w-full bg-secondary/50 border border-border text-foreground font-body text-sm px-4 py-3 focus:outline-none focus:border-gold/50 focus:ring-1 focus:ring-gold/30 transition-colors duration-300 placeholder:text-muted-foreground/50";

  return (
    <section className="py-24 md:py-32 relative">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_hsl(43_72%_55%_/_0.04),_transparent_60%)]" />

      <div className="container mx-auto px-6 md:px-12 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <p className="text-xs uppercase tracking-[0.4em] text-gold font-body font-medium mb-4">
            Let's Connect
          </p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-foreground leading-tight">
            Book a <span className="text-gradient-gold">Session</span>
          </h2>
          <p className="text-muted-foreground font-body text-base md:text-lg leading-relaxed mt-4 max-w-xl mx-auto">
            Have questions about the Academy, or want to explore a one-on-one session?
            Fill out the form below and Angela will personally follow up.
          </p>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.15 }}
          onSubmit={handleSubmit}
          className="max-w-xl mx-auto space-y-5"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="Your Name"
              required
              maxLength={200}
              className={inputClasses}
            />
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              placeholder="Email Address"
              required
              maxLength={320}
              className={inputClasses}
            />
          </div>
          <input
            type="tel"
            name="phone"
            value={form.phone}
            onChange={handleChange}
            placeholder="Phone (optional)"
            maxLength={30}
            className={inputClasses}
          />
          <textarea
            name="message"
            value={form.message}
            onChange={handleChange}
            placeholder="Tell me what you're looking for..."
            required
            maxLength={2000}
            rows={5}
            className={`${inputClasses} resize-none`}
          />
          <button
            type="submit"
            disabled={loading}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-gradient-gold text-primary-foreground font-body font-semibold text-sm uppercase tracking-[0.15em] px-10 py-4 hover:opacity-90 transition-opacity duration-300 shadow-gold disabled:opacity-50"
          >
            {loading ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send size={16} />
                Send Inquiry
              </>
            )}
          </button>
        </motion.form>
      </div>
    </section>
  );
};

export default InquirySection;
